/**
 * Tee choice contracts (preferred tee, §15 preferred tee decision): the tees a player may pick when
 * joining (public sign-up form) and in the player portal before the first round starts.
 */
import { z } from 'zod';
import { PreferredTeeSchema } from '../config/preferred-tee';

/** One tee of the tournament's main layout (round 1, primary course). */
export const TeeChoiceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string().nullable(),
  /** colour key the tee reads as (teeKeyOf), null when none of the five */
  key: PreferredTeeSchema.nullable(),
  ratings: z.array(
    z.object({
      gender: z.enum(['M', 'F']),
      courseRating: z.number(),
      slopeRating: z.number(),
      par: z.number(),
    }),
  ),
  /** sum of the hole distances of the layout's holes in metres (null when unknown) */
  lengthMeters: z.number().nullable(),
});
export type TeeChoice = z.infer<typeof TeeChoiceSchema>;

/** Tee choices of a tournament: the layout they belong to and its tees (hardest first). */
export const TeeChoicesSchema = z.object({
  courseId: z.uuid(),
  courseName: z.string(),
  tees: z.array(TeeChoiceSchema),
});
export type TeeChoices = z.infer<typeof TeeChoicesSchema>;

export const PlayerTeeLockReason = z.enum(['STARTED', 'CLOSED']);

/** GET /player/tee — the player's own tee choice (portal). */
export const PlayerTeeResponseSchema = z.object({
  /** Participant.teeBoxId — null = automatic (the tournament's tee rules) */
  teeBoxId: z.uuid().nullable(),
  /** null when the tournament has no layout with tees yet */
  choices: TeeChoicesSchema.nullable(),
  /** the player may change it: every round still SCHEDULED and the entry active */
  editable: z.boolean(),
  lockedReason: PlayerTeeLockReason.nullable(),
});
export type PlayerTeeResponse = z.infer<typeof PlayerTeeResponseSchema>;

/** PUT /player/tee — null = automatic */
export const PlayerTeeUpdateRequestSchema = z.object({ teeBoxId: z.uuid().nullable() });
export type PlayerTeeUpdateRequest = z.infer<typeof PlayerTeeUpdateRequestSchema>;

export const TeeErrorCode = {
  TEE_LOCKED: 'TEE_LOCKED',
} as const;
